/**
 * Scala .scl file parser
 * Parses Scala tuning files into TuningTable format.
 *
 * Format spec:
 * - `!` lines = comments (skip)
 * - First non-comment line = description
 * - Second non-comment line = N (number of pitches, NOT counting 1/1)
 * - Next N lines: pitches ascending. Decimal with `.` = cents, `p/q` = ratio
 * - Last pitch = period (stored as period_cents)
 */

import type { TuningTable } from '@/types/config';

export interface ScalaParseResult {
  valid: boolean;
  description?: string;
  tuningTable?: TuningTable;
  errors?: string[];
}

/**
 * Convert a ratio string "p/q" to 0.1-cent units.
 * @returns cents * 10 (int32)
 */
function ratioToCentsX10(num: number, den: number): number {
  if (den <= 0 || num <= 0) return 0;
  return Math.round(1200 * Math.log2(num / den) * 10);
}

/**
 * Parse a single pitch line from a .scl file.
 * - If it contains a `.`, treat as cents value
 * - If it contains `/`, treat as ratio p/q
 * - Plain integer is treated as ratio n/1
 * @returns { value, label } or null on parse error
 */
function parsePitchLine(line: string): { value: number; label: string } | null {
  const trimmed = line.trim();
  if (trimmed.length === 0) return null;

  if (trimmed.includes('.')) {
    // Cents value
    const cents = parseFloat(trimmed);
    if (isNaN(cents)) return null;
    return { value: Math.round(cents * 10), label: `${cents}c` };
  }

  if (trimmed.includes('/')) {
    // Ratio p/q
    const parts = trimmed.split('/');
    if (parts.length !== 2) return null;
    const num = parseInt(parts[0] ?? '', 10);
    const den = parseInt(parts[1] ?? '', 10);
    if (isNaN(num) || isNaN(den) || den <= 0 || num <= 0) return null;
    return { value: ratioToCentsX10(num, den), label: trimmed };
  }

  // Plain integer = ratio n/1
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return null;
  return { value: ratioToCentsX10(num, 1), label: `${num}/1` };
}

/**
 * Parse a Scala .scl file string into a TuningTable.
 */
export function parseScalaFile(content: string): ScalaParseResult {
  const errors: string[] = [];
  const lines = content.split(/\r?\n/);

  // Filter out comment lines (starting with !)
  const dataLines: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('!')) continue;
    dataLines.push(trimmed);
  }

  // Need at least 2 data lines (description + count)
  if (dataLines.length < 2) {
    return { valid: false, errors: ['File too short: need at least description and pitch count'] };
  }

  const description = dataLines[0] ?? '';

  const pitchCountStr = dataLines[1] ?? '';
  const pitchCount = parseInt(pitchCountStr, 10);
  if (isNaN(pitchCount)) {
    return { valid: false, errors: [`Invalid pitch count: "${pitchCountStr}"`] };
  }

  // Validate N range
  if (pitchCount < 1 || pitchCount > 128) {
    return { valid: false, errors: [`Pitch count ${pitchCount} out of range (1-128)`] };
  }

  // Parse N pitch lines
  const pitchLines = dataLines.slice(2);

  // Filter out empty lines from pitch section
  const nonEmptyPitchLines = pitchLines.filter((l) => l.length > 0);

  if (nonEmptyPitchLines.length < pitchCount) {
    return {
      valid: false,
      errors: [`Expected ${pitchCount} pitches, found ${nonEmptyPitchLines.length}`],
    };
  }

  const parsedPitches: { value: number; label: string }[] = [];
  for (let i = 0; i < pitchCount; i++) {
    const line = nonEmptyPitchLines[i] ?? '';
    const pitch = parsePitchLine(line);
    if (pitch === null) {
      errors.push(`Line ${i + 1}: cannot parse pitch "${line}"`);
      continue;
    }
    parsedPitches.push(pitch);
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  if (parsedPitches.length !== pitchCount) {
    return { valid: false, errors: ['Mismatch between expected and parsed pitch count'] };
  }

  // Last pitch = period, first N-1 pitches = scale degrees
  const lastParsed = parsedPitches[pitchCount - 1];
  const period_cents = lastParsed?.value ?? 12000;
  const innerPitches = parsedPitches.slice(0, pitchCount - 1);

  // Validate period range
  if (period_cents > 65535) {
    return { valid: false, errors: [`Period ${period_cents / 10} cents exceeds maximum (6553.5 cents)`] };
  }

  // Build pitches array: [0, ...innerPitches]
  // Degree 0 gets the period label (e.g. "2/1") to match Scale Workshop convention
  const pitches = [0, ...innerPitches.map((p) => p.value)];
  const pitch_labels = [lastParsed?.label ?? '1/1', ...innerPitches.map((p) => p.label)];
  const notes_per_period = pitches.length;

  // Validate ascending order
  for (let i = 1; i < pitches.length; i++) {
    const prev = pitches[i - 1];
    const curr = pitches[i];
    if (prev !== undefined && curr !== undefined && curr <= prev) {
      return { valid: false, errors: [`Pitches not ascending: degree ${i} (${curr / 10}c) <= degree ${i - 1} (${prev / 10}c)`] };
    }
  }

  const period_is_octave = Math.abs(period_cents - 12000) < 10;
  const scale_mask = Array.from({ length: notes_per_period }, () => true);

  const tuningTable: TuningTable = {
    notes_per_period,
    period_is_octave,
    period_cents,
    pitches,
    scale_mask,
    pitch_labels,
  };

  return {
    valid: true,
    description,
    tuningTable,
  };
}
